import React, { useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'
import { Collapse, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import Icon from '../../../../../atoms/Icon'
import StatusBadge from '../components/StatusBadge'
import SydActionMenu from '../../../../../commonDesign/SydActionMenu'

const useStyles = makeStyles(() => ({
  domainHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color .15s ease-in-out',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  chevron: {
    display: 'inline-flex',
    alignItems: 'center',
    color: 'var(--color-neutral-500)',
    transition: 'transform .2s ease-in-out',
    '&.__expanded': {
      transform: 'rotate(90deg)'
    }
  },
  connectionTile: {
    cursor: 'pointer',
    backgroundColor: 'var(--color-neutral-050)',
    transition: 'background-color .15s ease-in-out',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    },
    border: '2px solid transparent',
    '&.__selected': {
      border: '2px solid var(--color-primary-400)',
      '&:hover': {
        border: '2px solid var(--color-primary-300)'
      }
    }
  },
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  sectionDivider: {
    borderTop: 'var(--border-neutral-thin)'
  }
}))

function activateOnEnterOrSpace (handler) {
  return (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handler(event)
    }
  }
}

function DomainGroup ({ domain, domainName, groupConnections, selectedConnection, onSelect, connectionActions, defaultExpanded, forceExpanded }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const isExpanded = forceExpanded || expanded
  const hasUserAuth = useMemo(() => groupConnections.some(c => !!c.allowUserAuth), [groupConnections])
  const domainLabel = domainName || domain
  const headerId = `domain-group-${domain}`
  const panelId = `domain-group-panel-${domain}`

  const toggleExpanded = useCallback(() => {
    if (forceExpanded) return
    setExpanded(prev => !prev)
  }, [forceExpanded])

  return (
    <div className='flex flex-col'>
      <div
        id={headerId}
        role='button'
        tabIndex={forceExpanded ? -1 : 0}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        aria-label={`${domainLabel} integration group, ${groupConnections.length} connection${groupConnections.length === 1 ? '' : 's'}`}
        className={clsx(classes.domainHeader, 'flex gap-2 items-center justify-between px-2 py-1 radius-md')}
        onClick={toggleExpanded}
        onKeyDown={activateOnEnterOrSpace(toggleExpanded)}
      >
        <div className='flex items-center gap-2'>
          <span className={clsx(classes.chevron, { __expanded: isExpanded })} aria-hidden='true'>
            <Icon name='chevronRight' customSize={16} />
          </span>
          <span className='font-medium font-size-sm' title={domain}>{domainLabel}</span>
        </div>
        <div className='flex gap-2 items-center'>
          {hasUserAuth && <StatusBadge variant='primary'>User Auth</StatusBadge>}
          <span className='font-size-xs font-medium text-neutral-500 bg-neutral-200 radius-lg px-2'>
            {groupConnections.length}
          </span>
        </div>
      </div>
      <Collapse in={isExpanded} timeout={200}>
        <div
          id={panelId}
          role='group'
          aria-labelledby={headerId}
          className='flex flex-col gap-xs pt-1'
        >
          {groupConnections.map(conn => {
            const isSelected = selectedConnection?.connectionId === conn.connectionId
            const handleSelect = () => onSelect(conn)
            return (
              <div
                key={`connection_${conn.connectionId}`}
                role='button'
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={`Select ${conn.displayName} connection`}
                className={clsx(classes.connectionTile, 'flex justify-between items-center p-2 radius-sm', {
                  __selected: isSelected
                })}
                onClick={handleSelect}
                onKeyDown={activateOnEnterOrSpace(handleSelect)}
              >
                <div className='flex flex-col flex-1 gap-xs'>
                  <div>
                    <div className='font-size-sm font-medium'>{conn.displayName}</div>
                    <div className='font-size-xs opacity-lowlighted'>{conn.code}</div>
                  </div>
                  {!!conn.allowUserAuth && (
                    <div className='flex flex-wrap gap-xs'>
                      <StatusBadge variant='primary'>
                        User Auth{conn.userAuthCount > 0 ? ` · ${conn.userAuthCount}` : ''}
                      </StatusBadge>
                    </div>
                  )}
                </div>
                <SydActionMenu actions={connectionActions} subject={conn} />
              </div>
            )
          })}
        </div>
      </Collapse>
    </div>
  )
}

DomainGroup.propTypes = {
  domain: PropTypes.string.isRequired,
  domainName: PropTypes.string,
  groupConnections: PropTypes.array.isRequired,
  selectedConnection: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  connectionActions: PropTypes.array.isRequired,
  defaultExpanded: PropTypes.bool,
  forceExpanded: PropTypes.bool
}

export default DomainGroup
